/**
 * Schema Compare / Data Compare / Import Data / Create Database Blueprint.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11, including the two
 * `requestAnimationFrame` deferrals that let the Radix dropdown finish its
 * cleanup before a dialog opens — without them `pointer-events: none` sticks
 * on `document.body`.
 */
import { useState } from 'react';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { openDataImportForActiveConnection } from '../../workspace/data-import/openDataImportForActiveConnection';
import { useDatabaseBlueprintSetupStore } from '../../database-blueprint/store/databaseBlueprintSetupStore';
import type { UserFacingEngineId } from '../../database-blueprint/types/databaseBlueprintTypes';
import type { ToolbarActionContext } from './toolbarActionContext';

export function useToolbarCompareActions(ctx: ToolbarActionContext) {
  const { track, requestContext, activeConnections } = ctx;
  const [showDataCompareSetupDialog, setShowDataCompareSetupDialog] = useState(false);
  const [showSchemaCompareSetupDialog, setShowSchemaCompareSetupDialog] = useState(false);

  const handleOpenSchemaCompare = () => {
    addBreadcrumb('click', 'toolbar:schema-compare');
    track('toolbar:schema_compare');
    // Use requestAnimationFrame to allow Radix dropdown to cleanup before dialog opens
    // This prevents pointer-events: none getting stuck on document/body
    requestAnimationFrame(() => {
      setShowSchemaCompareSetupDialog(true);
    });
  };

  const handleOpenDataCompare = () => {
    addBreadcrumb('click', 'toolbar:data-compare');
    track('toolbar:data_compare');
    // Use requestAnimationFrame to allow Radix dropdown to cleanup before dialog opens
    requestAnimationFrame(() => {
      setShowDataCompareSetupDialog(true);
    });
  };

  const handleOpenImportData = () => {
    addBreadcrumb('click', 'toolbar:import-data');
    track('toolbar:import_data');
    openDataImportForActiveConnection();
  };

  const handleOpenDatabaseBlueprint = async () => {
    addBreadcrumb('click', 'toolbar:database-blueprint');
    track('toolbar:database_blueprint');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true },
      'Database Blueprint',
    );
    if (!context || !context.database) return;
    const ac = activeConnections.find((c) => c.id === context.connectionId);
    const engineId = (context.engine || ac?.connection.engine || 'mssql') as UserFacingEngineId;
    useDatabaseBlueprintSetupStore.getState().openSetupDialog({
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      database: context.database,
      engine: engineId,
    });
  };

  return {
    dataCompareDialog: { open: showDataCompareSetupDialog, setOpen: setShowDataCompareSetupDialog },
    schemaCompareDialog: { open: showSchemaCompareSetupDialog, setOpen: setShowSchemaCompareSetupDialog },
    actions: {
      'schema-compare': handleOpenSchemaCompare,
      'data-compare': handleOpenDataCompare,
      'import-data': handleOpenImportData,
      'database-blueprint': () => void handleOpenDatabaseBlueprint(),
    },
  };
}
